import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    BarGradient,
    Box,
    DispersedSourceSolidIcon,
    ExpandMoreIcon,
    Typography,
    useTheme,
    Link,
    Grid,
    MarkdownFormatter
} from '@methanesat/ui-components';
import { DataRowsModalBody } from '../../../EmissionsPageMap/LayerDataDrawer/Layout';
import { useGetTotalOnlyAccordionProps } from './utils';
import { TotalOnlyAccordionProps } from '../../../../types';
import { useTranslate } from '../../../../hooks';
import { useEffect } from 'react';
import { METHANESAT_FAQ_URL, FAQ_DISPERSED_SOURCES } from '../../../../consts';
import { EmissionTitle } from './EmissionTitle';
import { analytics, formatDateForUrl } from '../../../../utils';

export const TotalOnlyAccordion = ({ info, handleChange, expanded }: TotalOnlyAccordionProps) => {
    const theme = useTheme();
    const t = useTranslate();
    const { id, title, emissionsHeadline, dataRows, iconColor, platform, BarGradientPropValues } =
        useGetTotalOnlyAccordionProps(info);

    useEffect(() => {
        if (expanded) {
            analytics.expandTotalOnlyAccordion({
                latitude: info.coordinate?.[1],
                longitude: info.coordinate?.[0],
                date: formatDateForUrl(new Date(info.object.properties.datetime)),
                platform
            });
        }
    }, [expanded, info, platform]);

    const tooltip = (
        <Box sx={{ paddingBottom: 1 }}>
            <Typography variant="body2">
                {t(`emissionsMapPage.miniLegend.area-emission-raster.l4Description`)}
            </Typography>

            <Link
                href={`${METHANESAT_FAQ_URL}${FAQ_DISPERSED_SOURCES}`}
                underline="always"
                variant="body2"
                data-testid="emissions-faq-link"
                target="_blank"
                onClick={(event) => {
                    //prevent clicking the link from closing the accordion
                    event.stopPropagation();
                }}
            >
                {t(`emissionsMapPage.miniLegend.area-emission-raster.l4DescriptionLink`)}
            </Link>
        </Box>
    );

    return (
        <Accordion
            key={id}
            expanded={expanded}
            onChange={() => {
                handleChange(id, info);
            }}
            data-testid={`total-only-accordion-${id}`}
            elevation={0}
            disableGutters
            sx={theme.mixins.emissionAccordion}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ padding: '0' }}>
                <EmissionTitle
                    emissionTypeLabel={title}
                    tooltip={tooltip}
                    Icon={
                        <DispersedSourceSolidIcon
                            iconColor={iconColor}
                            striped={true}
                            sx={(theme) => ({ marginRight: theme.mixins.emissionsDrawerIcon.marginRight })}
                            width={theme.mixins.emissionsDrawerIcon.width}
                            height={theme.mixins.emissionsDrawerIcon.height}
                        />
                    }
                    emissionsHeadline={emissionsHeadline}
                    data-testid={`dispersed-accordion-title-${id}`}
                />
            </AccordionSummary>

            <AccordionDetails
                sx={(theme) => ({
                    pl: `calc(${theme.mixins.emissionsDrawerIcon.width}px + ${theme.mixins.emissionsDrawerIcon.marginRight})`
                })}
            >
                <Box sx={{ marginBottom: 2 }}>
                    <BarGradient {...BarGradientPropValues} />
                </Box>
                <Box marginBottom={1}>
                    <DataRowsModalBody dataRows={dataRows} />
                </Box>
                {/* Description */}
                <Grid size="auto" container paddingBottom={1} marginTop={1}>
                    <Grid size={{ xs: 12 }} data-testid={`total-only-description-${id}`}>
                        <Typography variant="body1" sx={{ paddingTop: 2 }}>
                            <MarkdownFormatter
                                markdown={t('emissionsMapPage.mapModals.dispersed.totalOnlyDescription', {
                                    link: `${METHANESAT_FAQ_URL}${FAQ_DISPERSED_SOURCES}`
                                })}
                                components={{
                                    a: ({ ...props }) => (
                                        <a
                                            {...props}
                                            style={{ color: 'inherit' }}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`dispersed-emissions-drawer-faq-link-${props.key}`}
                                            aria-label={`${t('emissionsMapPage.introDrawer.accessibility.forMoreInfoLink', { link: '' })}`}
                                        />
                                    )
                                }}
                            />
                        </Typography>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
